import React, { useEffect, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { Avatar } from '@/components/console/Avatar';
import { Switch } from '@/components/console/Switch';
import { useT, type LabelKey } from '@/i18n/labels';
import { useLangStore } from '@/stores/langStore';
import { useThemeStore } from '@/stores/themeStore';
import { useAgentStore } from '@/stores/agentStore';
import { usePrefsStore, type NotifPrefs } from '@/stores/prefsStore';
import { useAuthStore } from '@/stores/authStore';
import { useAuth } from '@/hooks/useAuth';
import { useConsole } from '@/console/ConsoleContext';
import { AVAIL_OPTIONS, availLabelKey } from '@/console/availability';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import type { AgentAvailability, AgentProfile } from '@/types';

const SetCard: React.FC<{ icon: string; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div className="ac-card ac-set-card">
    <div className="ac-set-card-head">
      <div className="ac-set-card-ico">
        <Icon name={icon} size={18} />
      </div>
      <div className="ac-set-card-title">{title}</div>
    </div>
    {children}
  </div>
);

export const SettingsPage: React.FC = () => {
  const { t } = useT();
  const lang = useLangStore((s) => s.lang);
  const setLang = useLangStore((s) => s.setLang);
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);
  const myAvailability = useAgentStore((s) => s.myAvailability);
  const user = useAuthStore((s) => s.user);
  const { logout } = useAuth();
  const { hub } = useConsole();

  const notif = usePrefsStore((s) => s.notif);
  const setNotif = usePrefsStore((s) => s.setNotif);
  const autoAssign = usePrefsStore((s) => s.autoAssign);
  const setAutoAssign = usePrefsStore((s) => s.setAutoAssign);
  const quickReplies = usePrefsStore((s) => s.quickReplies);
  const addQuickReply = usePrefsStore((s) => s.addQuickReply);
  const removeQuickReply = usePrefsStore((s) => s.removeQuickReply);

  const [maxChats, setMaxChats] = useState(8);
  const [savedToast, setSavedToast] = useState(false);
  const [saveError, setSaveError] = useState(false);

  // Load the current resolved concurrency cap for the slider.
  useEffect(() => {
    if (!user?.agentId) return;
    let cancelled = false;
    axiosClient
      .get<AgentProfile>(API.AGENTS.PROFILE(user.agentId))
      .then(({ data }) => {
        if (!cancelled && typeof data.maxConcurrentConversations === 'number') {
          setMaxChats(Math.max(1, data.maxConcurrentConversations));
        }
      })
      .catch(() => {
        /* keep default */
      });
    return () => {
      cancelled = true;
    };
  }, [user?.agentId]);

  const chooseAvailability = async (a: AgentAvailability) => {
    try {
      await hub.setAvailability(a);
    } catch {
      /* hub offline — retry when live */
    }
  };

  const save = async () => {
    setSaveError(false);
    try {
      if (user?.agentId) {
        await axiosClient.put(API.AGENTS.PROFILE(user.agentId), {
          maxConcurrentConversations: maxChats,
        });
      }
      setSavedToast(true);
      window.setTimeout(() => setSavedToast(false), 2600);
    } catch {
      // PUT is admin-gated (users.manage); a regular agent may be forbidden.
      setSaveError(true);
      window.setTimeout(() => setSaveError(false), 3000);
    }
  };

  const notifRows: Array<{ key: keyof NotifPrefs; label: LabelKey }> = [
    { key: 'desktop', label: 'set_notif_desktop' },
    { key: 'sound', label: 'set_notif_sound' },
    { key: 'new', label: 'set_notif_new' },
    { key: 'consult', label: 'set_notif_consult' },
    { key: 'sla', label: 'set_notif_sla' },
  ];

  return (
    <div className="ac-page ac-fade-up" data-testid="settings-page">
      <div className="ac-page-head">
        <h1 className="ac-page-title">{t('set_title')}</h1>
        <div className="ac-page-sub">{t('set_sub')}</div>
      </div>

      <div className="ac-settings ac-stagger">
        {/* Profile */}
        <SetCard icon="user" title={t('set_profile')}>
          <div className="ac-profile-row">
            <Avatar name={user?.displayName} seed={user?.agentId} size={64} className="ac-profile-av" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 17, fontWeight: 800 }}>{user?.displayName || t('role_agent')}</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('role_agent')}</div>
            </div>
          </div>
          <div className="ac-set-grid">
            <div className="ac-set-field-group">
              <label className="ac-set-field-label">{t('set_name')}</label>
              <input className="ac-field" defaultValue={user?.displayName ?? ''} readOnly />
            </div>
            <div className="ac-set-field-group">
              <label className="ac-set-field-label">{t('set_email')}</label>
              <input className="ac-field" defaultValue={user?.email ?? ''} readOnly />
            </div>
          </div>
        </SetCard>

        {/* Availability & capacity */}
        <SetCard icon="bell" title={t('set_avail_cap')}>
          <div className="ac-set-row">
            <div className="ac-set-row-main">
              <div className="ac-set-label">{t('set_default_status')}</div>
            </div>
            <div className="ac-set-control">
              <select
                className="ac-field"
                value={myAvailability}
                onChange={(e) => chooseAvailability(e.target.value as AgentAvailability)}
                data-testid="settings-availability"
              >
                {AVAIL_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {t(availLabelKey(o))}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="ac-set-row">
            <div className="ac-set-row-main">
              <div className="ac-set-label">{t('set_max_chats')}</div>
              <div className="ac-set-help">{t('set_max_chats_help')}</div>
            </div>
            <div className="ac-set-control ac-range">
              <input
                type="range"
                min="1"
                max="10"
                value={maxChats}
                onChange={(e) => setMaxChats(+e.target.value)}
                data-testid="settings-maxchats"
              />
              <span className="ac-range-val">{maxChats}</span>
            </div>
          </div>
          <div className="ac-set-row">
            <div className="ac-set-row-main">
              <div className="ac-set-label">{t('set_autoassign')}</div>
              <div className="ac-set-help">{t('set_autoassign_sub')}</div>
            </div>
            <Switch on={autoAssign} onChange={setAutoAssign} data-testid="settings-autoassign" />
          </div>
        </SetCard>

        {/* Notifications */}
        <SetCard icon="bell" title={t('set_notif')}>
          {notifRows.map((r) => (
            <div className="ac-set-row" key={r.key}>
              <div className="ac-set-row-main">
                <div className="ac-set-label">{t(r.label)}</div>
              </div>
              <Switch on={notif[r.key]} onChange={(v) => setNotif(r.key, v)} data-testid={`settings-notif-${r.key}`} />
            </div>
          ))}
        </SetCard>

        {/* Appearance — mirrors chrome */}
        <SetCard icon="sun" title={t('set_appearance')}>
          <div className="ac-set-row">
            <div className="ac-set-row-main">
              <div className="ac-set-label">{t('set_theme')}</div>
            </div>
            <div className="ac-seg">
              <button className={mode === 'light' ? 'active' : ''} onClick={() => setMode('light')} data-testid="settings-theme-light">
                <Icon name="sun" size={14} />
                {t('theme_light')}
              </button>
              <button className={mode === 'dark' ? 'active' : ''} onClick={() => setMode('dark')} data-testid="settings-theme-dark">
                <Icon name="moon" size={14} />
                {t('theme_dark')}
              </button>
            </div>
          </div>
          <div className="ac-set-row">
            <div className="ac-set-row-main">
              <div className="ac-set-label">{t('set_language')}</div>
            </div>
            <div className="ac-seg">
              <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>
                English
              </button>
              <button className={lang === 'he' ? 'active' : ''} onClick={() => setLang('he')}>
                עברית
              </button>
            </div>
          </div>
        </SetCard>

        {/* Quick replies */}
        <SetCard icon="sparkle" title={t('set_quick')}>
          <div style={{ padding: '12px 22px 4px', fontSize: 12, color: 'var(--text-muted)' }}>{t('set_quick_sub')}</div>
          {quickReplies.map((q, i) => (
            <div className="ac-quick-row" key={i}>
              <Icon name="chat" size={15} style={{ color: 'var(--text-faint)', flexShrink: 0 }} />
              <div className="ac-quick-text">{q}</div>
              <button className="ac-quick-del" onClick={() => removeQuickReply(i)} aria-label={t('set_quick_delete')}>
                <Icon name="x" size={14} />
              </button>
            </div>
          ))}
          <div style={{ padding: '14px 22px' }}>
            <button className="ac-btn ghost sm" onClick={() => addQuickReply(t('set_quick_new'))} data-testid="settings-add-reply">
              <Icon name="plus" size={15} />
              {t('set_add_reply')}
            </button>
          </div>
        </SetCard>

        {/* Security */}
        <SetCard icon="release" title={t('set_security')}>
          <div className="ac-set-row">
            <div className="ac-set-row-main">
              <div className="ac-set-label">{t('set_change_pw')}</div>
            </div>
            <button className="ac-btn ghost sm" disabled title={t('set_soon')}>
              {t('set_soon')}
            </button>
          </div>
          <div className="ac-set-row">
            <div className="ac-set-row-main">
              <div className="ac-set-label">{t('set_signout_all')}</div>
            </div>
            <button className="ac-btn danger-ghost sm" onClick={() => logout()} data-testid="settings-signout">
              <Icon name="logout" size={15} className="ac-flip" />
              {t('set_signout_all')}
            </button>
          </div>
        </SetCard>

        <div className="ac-set-footer">
          {saveError && (
            <span role="alert" style={{ color: 'var(--danger)', fontWeight: 600, fontSize: 12.5, alignSelf: 'center' }}>
              {t('set_save_error')}
            </span>
          )}
          {/* Inline confirmation right next to the button — the corner toast alone was easy to
              miss, so the agent gets unambiguous feedback where they clicked. */}
          {savedToast && !saveError && (
            <span
              role="status"
              data-testid="settings-saved-inline"
              style={{ color: 'var(--success)', fontWeight: 700, fontSize: 12.5, alignSelf: 'center', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Icon name="check" size={15} />
              {t('set_saved')}
            </span>
          )}
          <button className="ac-btn primary" onClick={save} data-testid="settings-save">
            <Icon name="check" size={16} />
            {t('set_save')}
          </button>
        </div>
      </div>

      {savedToast && (
        <div className="ac-toast" role="status" data-testid="settings-saved-toast">
          <Icon name="check" size={16} />
          {t('set_saved')}
        </div>
      )}
    </div>
  );
};
